import React, { useMemo, useState } from "react";
import "../UserManagement/UserManagement.scss";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import { ChevronDown } from "../../assets/Icons/ChevronDown";
import { CloseIcon } from "../../assets/Icons/CloseIcon";

const readValue = (value) => String(value ?? "").trim();

// Filter keys in display order; `getValue` reads the matching field from an order
export const ORDER_FILTER_CONFIG = [
  {
    key: "region",
    label: "Region/Country",
    getValue: (order) => readValue(order.country),
  },
  {
    key: "status",
    label: " Order Status",
    getValue: (order) => readValue(order.status),
  },
  {
    key: "analyzer",
    label: "Analyzer Type",
    getValue: (order) => readValue(order.analyzer),
  },
  {
    key: "client",
    label: "Client",
    getValue: (order) => readValue(order.clientEntity),
  },
];

export const EMPTY_ORDER_FILTERS = ORDER_FILTER_CONFIG.reduce(
  (filters, { key }) => ({ ...filters, [key]: [] }),
  {},
);

export const hasActiveOrderFilters = (filters) =>
  Object.values(filters || {}).some((values) => values.length > 0);

// Keeps orders matching every selected filter, optionally ignoring one key (used for option lists)
export const filterOrdersByFilters = (orders, filters, ignoreKey = null) =>
  orders.filter((order) =>
    ORDER_FILTER_CONFIG.every(({ key, getValue }) => {
      const selected = filters[key] || [];
      return key === ignoreKey || !selected.length || selected.includes(getValue(order));
    }),
  );

// Options cascade from earlier filters only, preserving selections already made upstream.
const getOptions = (orders, filters, key) => {
  const filterIndex = ORDER_FILTER_CONFIG.findIndex(
    (config) => config.key === key,
  );
  const previousFilters = ORDER_FILTER_CONFIG.slice(0, filterIndex);
  const availableOrders = orders.filter((order) =>
    previousFilters.every(({ key: previousKey, getValue: getPreviousValue }) => {
      const selected = filters[previousKey] || [];
      return !selected.length || selected.includes(getPreviousValue(order));
    }),
  );
  const { getValue } = ORDER_FILTER_CONFIG[filterIndex];
  const values = availableOrders
    .map(getValue)
    .filter((value) => value && value !== "-");
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
};

// Changing a filter can invalidate later selections, but must never reset earlier ones.
const pruneDownstreamFilters = (orders, filters, changedKey) => {
  const changedIndex = ORDER_FILTER_CONFIG.findIndex(
    (config) => config.key === changedKey,
  );
  let next = { ...filters };

  ORDER_FILTER_CONFIG.slice(changedIndex + 1).forEach(({ key }) => {
    const options = getOptions(orders, next, key);
    const kept = next[key].filter((value) => options.includes(value));
    if (kept.length !== next[key].length) {
      next = { ...next, [key]: kept };
    }
  });

  return next;
};

// Side panel with cascading order filters (Region, Status, Analyzer Type, Client)
export const OrdersFilterPanel = ({ orders, appliedFilters, onApply, onClose }) => {
  const [tempFilters, setTempFilters] = useState({ ...appliedFilters });
  const [expanded, setExpanded] = useState(() =>
    ORDER_FILTER_CONFIG.reduce((acc, { key }) => ({ ...acc, [key]: true }), {}),
  );
  const [searchTerms, setSearchTerms] = useState(() =>
    ORDER_FILTER_CONFIG.reduce((acc, { key }) => ({ ...acc, [key]: "" }), {}),
  );

  const optionsByKey = useMemo(
    () =>
      ORDER_FILTER_CONFIG.reduce(
        (acc, { key }) => ({ ...acc, [key]: getOptions(orders, tempFilters, key) }),
        {},
      ),
    [orders, tempFilters],
  );

  const totalSelected = Object.values(tempFilters).reduce(
    (count, values) => count + values.length,
    0,
  );

  const updateFilters = (nextFilters, changedKey) =>
    setTempFilters(
      pruneDownstreamFilters(orders, nextFilters, changedKey),
    );

  const toggleValue = (key, value) => {
    const current = tempFilters[key];
    updateFilters(
      {
        ...tempFilters,
        [key]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      },
      key,
    );
  };

  const toggleAll = (key) => {
    const options = optionsByKey[key];
    const allSelected =
      options.length > 0 && options.every((o) => tempFilters[key].includes(o));
    updateFilters(
      { ...tempFilters, [key]: allSelected ? [] : [...options] },
      key,
    );
  };

  const renderAccordion = ({ key, label }) => {
    const options = optionsByKey[key];
    const term = searchTerms[key].trim().toLowerCase();
    const visibleOptions = term
      ? options.filter((option) => option.toLowerCase().includes(term))
      : options;
    const allSelected =
      options.length > 0 && options.every((o) => tempFilters[key].includes(o));

    return (
      <div className="filter-accordion" key={key}>
        <div
          className="filter-accordion-header"
          onClick={() => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
        >
          <span className="filter-accordion-title">{label}</span>
          <ChevronDown
            width={20}
            height={20}
            color="#dedee2"
            className={`accordion-chevron ${expanded[key] ? "expanded" : ""}`}
          />
        </div>
        {expanded[key] && (
          <div className="filter-accordion-content">
            <div className="filter-search-container">
              <SearchIcon width={16} height={16} color="#737387" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerms[key]}
                onChange={(event) =>
                  setSearchTerms((prev) => ({ ...prev, [key]: event.target.value }))
                }
                className="filter-search-input"
              />
            </div>
            <div className="filter-options">
              {options.length > 0 && (
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => toggleAll(key)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="filter-option-label">Select All</span>
                </label>
              )}
              {visibleOptions.map((option) => (
                <label key={option} className="filter-option">
                  <input
                    type="checkbox"
                    checked={tempFilters[key].includes(option)}
                    onChange={() => toggleValue(key, option)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="filter-option-label">{option}</span>
                </label>
              ))}
              {options.length === 0 && (
                <span className="filter-no-options">No options available</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="filter-panel orders-filter-panel">
      <div className="filter-panel-header">
        <h2 className="filter-panel-title">Filters</h2>
        <button
          type="button"
          className="filter-clear-button"
          onClick={() => setTempFilters({ ...EMPTY_ORDER_FILTERS })}
        >
          Clear
        </button>
      </div>
      <p className="filter-panel-subtitle">Select filters below</p>

      <div className="filter-accordions">
        {ORDER_FILTER_CONFIG.map(renderAccordion)}
      </div>

      <div className="filter-panel-footer">
        <div className="filter-selected-info">
          <button
            type="button"
            className="clear-selection-button"
            aria-label="Clear selection"
            onClick={() => setTempFilters({ ...EMPTY_ORDER_FILTERS })}
          >
            <CloseIcon width={14} height={14} color="#dedee2" />
          </button>
          <span>Selected {totalSelected} items</span>
        </div>
        <div className="filter-panel-actions">
          <button type="button" className="filter-cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="filter-apply-button"
            onClick={() => onApply(tempFilters)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
